import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { 
  Users, Search, UserPlus, Mail, Trash2, Eye, 
  AlertTriangle, CheckCircle, Clock, XCircle, 
  RefreshCw, Send, ChevronDown, ChevronUp, Edit2, MessageCircle, X,
  Plus, Phone, Bell, UserX, UserCheck, Filter
} from 'lucide-react';
import './CompanyEmployees.css';

const CompanyEmployees = ({ companyUser }) => {
  const [employees, setEmployees] = useState([]);
  const [permitEmployees, setPermitEmployees] = useState([]); // Employees from permits
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'terminated', 'all'
  const [slots, setSlots] = useState({ limit: 50, used: 0, remaining: 50, show_warning: false });
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [changeRequests, setChangeRequests] = useState([]);
  const [showChangeRequests, setShowChangeRequests] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    full_name: '',
    email: '',
    phone: ''
  });
  
  // Edit employee state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  
  // Send message state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageEmployee, setMessageEmployee] = useState(null);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // View permits state
  const [viewingEmployee, setViewingEmployee] = useState(null);
  
  // Notify modal state (SMS or Email choice)
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyEmployee, setNotifyEmployee] = useState(null);
  const [notifyMethod, setNotifyMethod] = useState('email'); // 'email' or 'sms'
  const [notifyForm, setNotifyForm] = useState({ subject: '', message: '' });
  const [sendingNotification, setSendingNotification] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchPermitEmployees();
    fetchSlots();
    fetchChangeRequests();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/company/employees');
      setEmployees(response.data);
    } catch (error) {
      console.log('No invited employees found');
    } finally {
      setLoading(false);
    }
  };

  // Fetch unique employees from permits (by employee_name)
  const fetchPermitEmployees = async () => {
    try {
      const response = await axiosInstance.get('/permits');
      const permits = response.data;
      
      // Extract unique employees from permits
      const employeeMap = new Map();
      permits.forEach(permit => {
        const empName = permit.employee_name?.trim();
        if (empName) {
          const key = empName.toLowerCase();
          if (!employeeMap.has(key)) {
            employeeMap.set(key, {
              id: `permit-emp-${empName.replace(/\s+/g, '-').toLowerCase()}`,
              full_name: empName,
              email: null,
              phone: null,
              invite_status: 'from_permits',
              employee_status: permit.employee_status || 'active',
              permits: [],
              permit_count: 0
            });
          }
          employeeMap.get(key).permits.push(permit);
          employeeMap.get(key).permit_count++;
          // If any permit is terminated, mark employee as terminated
          if (permit.employee_status === 'terminated') {
            employeeMap.get(key).employee_status = 'terminated';
          }
        }
      });
      
      setPermitEmployees(Array.from(employeeMap.values()));
    } catch (error) {
      console.log('Failed to fetch permit employees');
    }
  };

  // Combine both employee sources
  const allEmployees = React.useMemo(() => {
    const combined = [...employees];
    
    // Add permit-based employees that aren't already in the invited list
    permitEmployees.forEach(pe => {
      const exists = employees.some(e => 
        e.full_name?.toLowerCase() === pe.full_name?.toLowerCase()
      );
      if (!exists) {
        combined.push(pe);
      }
    });
    
    return combined;
  }, [employees, permitEmployees]);

  // Real-time filtering of employees (using combined allEmployees)
  const filteredEmployees = allEmployees.filter((employee) => {
    // Status filter
    const empStatus = employee.employee_status || 'active';
    if (statusFilter === 'active' && empStatus === 'terminated') return false;
    if (statusFilter === 'terminated' && empStatus !== 'terminated') return false;
    
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      employee.full_name?.toLowerCase().includes(query) ||
      employee.email?.toLowerCase().includes(query)
    );
  });

  const fetchSlots = async () => {
    try {
      const response = await axiosInstance.get('/company/employee-slots');
      setSlots(response.data);
    } catch (error) {
      console.error('Failed to load slots:', error);
    }
  };

  const fetchChangeRequests = async () => {
    try {
      const response = await axiosInstance.get('/company/change-requests');
      setChangeRequests(response.data.filter(r => r.status === 'pending'));
    } catch (error) {
      console.error('Failed to load change requests:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);

    try {
      const response = await axiosInstance.post('/company/invite-employee', inviteForm);
      toast.success('Invitation sent successfully!');
      setInviteForm({ full_name: '', email: '', phone: '' });
      setShowInviteForm(false);
      fetchEmployees();
      setSlots({
        ...slots,
        used: slots.used + 1,
        remaining: response.data.remaining_slots,
        show_warning: response.data.show_warning,
        warning_message: response.data.warning_message
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (employeeId) => {
    try {
      await axiosInstance.post(`/company/resend-invite/${employeeId}`);
      toast.success('Invitation resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleDisableEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to disable this employee account?')) return;
    
    try {
      await axiosInstance.post(`/company/employees/${employeeId}/disable`);
      toast.success('Employee disabled');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable employee');
    }
  };

  const handleEnableEmployee = async (employeeId) => {
    try {
      await axiosInstance.post(`/company/employees/${employeeId}/enable`);
      toast.success('Employee enabled');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable employee');
    }
  };

  const handleRemoveEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to permanently remove ${employeeName}? This will delete all their permits.`)) return;
    
    try {
      await axiosInstance.delete(`/company/employees/${employeeId}`);
      toast.success('Employee removed');
      fetchEmployees();
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove employee');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axiosInstance.post(`/company/change-requests/${requestId}/approve`);
      toast.success('Request approved');
      fetchChangeRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axiosInstance.post(`/company/change-requests/${requestId}/reject`);
      toast.success('Request rejected');
      fetchChangeRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  // Edit employee handlers
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEditForm({
      full_name: employee.full_name || '',
      email: employee.email || '',
      phone: employee.phone || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.put(`/company/employees/${editingEmployee.id}`, editForm);
      toast.success('Employee updated successfully!');
      setShowEditModal(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  // Send message handlers
  const handleOpenMessage = (employee) => {
    setMessageEmployee(employee);
    setMessageForm({
      subject: 'Permit Renewal Reminder',
      message: `Hi ${employee.full_name},\n\nThis is a reminder that one or more of your permits are expiring soon. Please take action to renew them as soon as possible.\n\nThank you,\n${companyUser?.company_name || 'Your Company'}`
    });
    setShowMessageModal(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSendingMessage(true);
    try {
      await axiosInstance.post(`/company/employees/${messageEmployee.id}/send-message`, {
        subject: messageForm.subject,
        message: messageForm.message
      });
      toast.success(`Message sent to ${messageEmployee.full_name}!`);
      setShowMessageModal(false);
      setMessageEmployee(null);
      setMessageForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // View employee permits
  const handleViewPermits = (employee) => {
    setViewingEmployee(employee);
  };

  // Notify employee (open modal with SMS/Email choice)
  const handleNotify = (employee) => {
    setNotifyEmployee(employee);
    setNotifyMethod('email');
    setNotifyForm({
      subject: 'Permit Renewal Reminder',
      message: `Hi ${employee.full_name},\n\nThis is a reminder that one or more of your permits are expiring soon. Please take action to renew them as soon as possible.\n\nThank you,\n${companyUser?.company_name || 'Your Company'}`
    });
    setShowNotifyModal(true);
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    setSendingNotification(true);
    try {
      await axiosInstance.post(`/company/employees/${notifyEmployee.id}/notify`, {
        method: notifyMethod,
        subject: notifyForm.subject,
        message: notifyForm.message
      });
      toast.success(`${notifyMethod === 'sms' ? 'SMS' : 'Email'} sent to ${notifyEmployee.full_name}!`);
      setShowNotifyModal(false);
      setNotifyEmployee(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${notifyMethod}`);
    } finally {
      setSendingNotification(false);
    }
  };

  // Terminate employee handler
  const handleTerminateEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to terminate ${employee.full_name}? Their data will be kept for future reference.`)) return;
    
    try {
      if (employee.invite_status === 'from_permits') {
        // Terminate permit-based employee
        await axiosInstance.post('/company/permit-employees/terminate', {
          employee_name: employee.full_name
        });
      } else {
        // Terminate invited employee
        await axiosInstance.post(`/company/employees/${employee.id}/terminate`);
      }
      toast.success(`${employee.full_name} has been terminated`);
      fetchEmployees();
      fetchPermitEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate employee');
    }
  };

  // Reactivate employee handler
  const handleReactivateEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to reactivate ${employee.full_name}?`)) return;
    
    try {
      if (employee.invite_status === 'from_permits') {
        // Reactivate permit-based employee
        await axiosInstance.post('/company/permit-employees/reactivate', {
          employee_name: employee.full_name
        });
      } else {
        // Reactivate invited employee
        await axiosInstance.post(`/company/employees/${employee.id}/reactivate`);
      }
      toast.success(`${employee.full_name} has been reactivated`);
      fetchEmployees();
      fetchPermitEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate employee');
    }
  };

  const getStatusBadge = (status, employeeStatus) => {
    // Check if employee is terminated first
    if (employeeStatus === 'terminated') {
      return <span className="status-badge status-terminated"><UserX size={14} /> Terminated</span>;
    }
    
    switch (status) {
      case 'accepted':
        return <span className="status-badge status-active"><CheckCircle size={14} /> Active</span>;
      case 'pending':
        return <span className="status-badge status-pending"><Clock size={14} /> Pending</span>;
      case 'disabled':
        return <span className="status-badge status-disabled"><XCircle size={14} /> Disabled</span>;
      case 'from_permits':
        return <span className="status-badge status-permits"><Eye size={14} /> Tracked</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  // Count active vs terminated
  const activeCount = allEmployees.filter(e => (e.employee_status || 'active') !== 'terminated').length;
  const terminatedCount = allEmployees.filter(e => e.employee_status === 'terminated').length;

  return (
    <div className="company-employees">
      {/* Header with Slots Warning */}
      <div className="employees-header">
        <div className="header-left">
          <h2><Users size={24} /> Employee Management</h2>
          <span className="employee-count">{allEmployees.length} employees</span>
        </div>
        <div className="header-right">
          <div className={`slots-indicator ${slots.show_warning ? 'warning' : ''}`}>
            <span className="slots-label">Employee Slots:</span>
            <span className="slots-count">{slots.used} / {slots.limit}</span>
            {slots.show_warning && (
              <span className="slots-warning">
                <AlertTriangle size={14} />
                {slots.warning_message || `ONLY ${slots.remaining} SLOTS REMAIN`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Change Requests Banner */}
      {changeRequests.length > 0 && (
        <div className="change-requests-banner" onClick={() => setShowChangeRequests(!showChangeRequests)}>
          <div className="banner-content">
            <AlertTriangle size={18} />
            <span>{changeRequests.length} pending change request{changeRequests.length !== 1 ? 's' : ''}</span>
          </div>
          {showChangeRequests ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      )}

      {/* Change Requests Dropdown */}
      {showChangeRequests && changeRequests.length > 0 && (
        <div className="change-requests-list">
          {changeRequests.map((request) => (
            <div key={request.id} className="change-request-item">
              <div className="request-info">
                <strong>{request.employee_name}</strong>
                <span className="request-type">{request.request_type.toUpperCase()}</span>
                <span className="request-permit">Permit #{request.permit_number}</span>
              </div>
              <p className="request-description">{request.description}</p>
              <div className="request-actions">
                <button className="btn-approve" onClick={() => handleApproveRequest(request.id)}>
                  <CheckCircle size={14} /> Approve
                </button>
                <button className="btn-reject" onClick={() => handleRejectRequest(request.id)}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search and Invite */}
      <div className="employees-actions">
        <div className="search-form">
          <div className="search-input-wrapper">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                type="button" 
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
        </div>
        
        {/* Status Filter Dropdown */}
        <div className="status-filter-wrapper">
          <Filter size={18} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter-select"
            data-testid="employee-status-filter"
          >
            <option value="active">Active ({activeCount})</option>
            <option value="terminated">Terminated ({terminatedCount})</option>
            <option value="all">All Employees ({allEmployees.length})</option>
          </select>
        </div>
        
        <button 
          className="btn-invite" 
          onClick={() => setShowInviteForm(!showInviteForm)}
          disabled={slots.remaining <= 0}
        >
          <UserPlus size={18} />
          Invite Employee
        </button>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="invite-form-container">
          <h3><Mail size={20} /> Invite New Employee</h3>
          <form onSubmit={handleInvite} className="invite-form">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({...inviteForm, full_name: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                  placeholder="+1 555 123 4567"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowInviteForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-send-invite" disabled={inviting}>
                <Send size={16} />
                {inviting ? 'Sending...' : 'SEND INVITE'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employees Table */}
      <div className="employees-table-container">
        {loading ? (
          <div className="loading-state">Loading employees...</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            {allEmployees.length === 0 ? (
              <>
                <h3>No employees yet</h3>
                <p>Add permits with employee names or invite employees to get started</p>
              </>
            ) : statusFilter === 'terminated' && terminatedCount === 0 ? (
              <>
                <h3>No terminated employees</h3>
                <p>All employees are currently active</p>
              </>
            ) : statusFilter === 'active' && activeCount === 0 ? (
              <>
                <h3>No active employees</h3>
                <p>All employees have been terminated</p>
              </>
            ) : (
              <>
                <h3>No results found</h3>
                <p>No employees match &quot;{searchQuery}&quot;</p>
              </>
            )}
          </div>
        ) : (
          <table className="employees-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Permits</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => {
                const isTerminated = employee.employee_status === 'terminated';
                return (
                <tr key={employee.id} className={`${employee.invite_status === 'disabled' ? 'disabled-row' : ''} ${isTerminated ? 'terminated-row' : ''}`}>
                  <td className="employee-name-cell">
                    <span className="employee-name clickable" onClick={() => handleViewPermits(employee)}>
                      {employee.full_name}
                    </span>
                    {employee.invite_status === 'from_permits' && (
                      <span className="from-permits-badge">From Permits</span>
                    )}
                  </td>
                  <td className="employee-email">{employee.email || <span className="empty-value">Not provided</span>}</td>
                  <td className="employee-phone">{employee.phone || <span className="empty-value">Not provided</span>}</td>
                  <td>{getStatusBadge(employee.invite_status, employee.employee_status)}</td>
                  <td className="permit-count">{employee.permit_count || 0}</td>
                  <td className="actions-cell">
                    <div className="action-buttons">
                      {/* View Permits */}
                      <button
                        className="btn-icon btn-view"
                        onClick={() => handleViewPermits(employee)}
                        title="View Permits"
                        data-testid={`view-permits-${employee.id}`}
                      >
                        <Eye size={16} />
                      </button>
                      
                      {/* Edit Employee (only for invited employees, not terminated) */}
                      {employee.invite_status !== 'from_permits' && !isTerminated && (
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEditEmployee(employee)}
                          title="Edit Employee"
                          data-testid={`edit-employee-${employee.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      
                      {/* Notify (SMS/Email) - only for active employees */}
                      {!isTerminated && (
                        <button
                          className="btn-icon btn-notify"
                          onClick={() => handleNotify(employee)}
                          title="Notify Employee (SMS/Email)"
                          data-testid={`notify-employee-${employee.id}`}
                        >
                          <Bell size={16} />
                        </button>
                      )}
                      
                      {/* Add Permit - only for active employees */}
                      {!isTerminated && (
                        <button
                          className="btn-icon btn-add"
                          onClick={() => {
                            // Store the employee name to pre-fill in add permit modal
                            window.localStorage.setItem('prefill_employee', employee.full_name);
                            toast.info(`Click "ADD NEW PERSON TO TRACK" to add a permit for ${employee.full_name}`);
                          }}
                          title="Add Permit"
                          data-testid={`add-permit-${employee.id}`}
                        >
                          <Plus size={16} />
                        </button>
                      )}
                      
                      {/* Terminate Employee - only for active employees */}
                      {!isTerminated && (
                        <button
                          className="btn-icon btn-terminate"
                          onClick={() => handleTerminateEmployee(employee)}
                          title="Terminate Employee"
                          data-testid={`terminate-employee-${employee.id}`}
                        >
                          <UserX size={16} />
                        </button>
                      )}
                      
                      {/* Reactivate Employee - only for terminated employees */}
                      {isTerminated && (
                        <button
                          className="btn-icon btn-reactivate"
                          onClick={() => handleReactivateEmployee(employee)}
                          title="Reactivate Employee"
                          data-testid={`reactivate-employee-${employee.id}`}
                        >
                          <UserCheck size={16} />
                        </button>
                      )}
                      
                      {/* Delete Employee - always available */}
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleRemoveEmployee(employee.id, employee.full_name)}
                        title="Delete Employee"
                        data-testid={`delete-employee-${employee.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Employee Modal */}
      {showEditModal && editingEmployee && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-employee-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Employee</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveEmployee}>
              <div className="form-group">
                <label className="label">Full Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Phone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {showMessageModal && messageEmployee && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Message to {messageEmployee.full_name}</h2>
              <button className="close-btn" onClick={() => setShowMessageModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSendMessage}>
              <div className="form-group">
                <label className="label">Subject</label>
                <input
                  type="text"
                  className="input-field"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Message</label>
                <textarea
                  className="input-field message-textarea"
                  rows={6}
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                />
              </div>
              <p className="message-info">
                <Mail size={14} /> Message will be sent to: {messageEmployee.email}
              </p>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowMessageModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary btn-send" disabled={sendingMessage}>
                  <Send size={16} />
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Permits Modal */}
      {viewingEmployee && (
        <div className="modal-overlay" onClick={() => setViewingEmployee(null)}>
          <div className="modal-content view-permits-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Permits for {viewingEmployee.full_name}</h2>
              <button className="close-btn" onClick={() => setViewingEmployee(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {viewingEmployee.permits && viewingEmployee.permits.length > 0 ? (
                <div className="permits-list">
                  {viewingEmployee.permits.map((permit, idx) => (
                    <div key={idx} className="permit-list-item">
                      <div className="permit-list-header">
                        <span className="permit-list-type">
                          {permit.permit_type === 'firearms' ? '🔫 BSIS Exposed Firearm' : 
                           permit.permit_type === 'guard_card' ? '🛡️ Guard Card' : 
                           permit.permit_type === 'ccw' ? '🔒 CCW' : permit.permit_type}
                        </span>
                        <span className={`permit-list-status ${new Date(permit.expiration_date) < new Date() ? 'expired' : 'active'}`}>
                          {new Date(permit.expiration_date) < new Date() ? 'EXPIRED' : 'ACTIVE'}
                        </span>
                      </div>
                      <div className="permit-list-details">
                        <p><strong>Permit #:</strong> {permit.permit_number}</p>
                        <p><strong>Issued:</strong> {new Date(permit.issue_date).toLocaleDateString()}</p>
                        <p><strong>Expires:</strong> {new Date(permit.expiration_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-permits-msg">No permits found for this employee.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notify Modal (SMS/Email) */}
      {showNotifyModal && notifyEmployee && (
        <div className="modal-overlay" onClick={() => setShowNotifyModal(false)}>
          <div className="modal-content notify-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Notify {notifyEmployee.full_name}</h2>
              <button className="close-btn" onClick={() => setShowNotifyModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSendNotification}>
              <div className="form-group">
                <label className="label">Notification Method</label>
                <div className="notify-method-buttons">
                  <button
                    type="button"
                    className={`notify-method-btn ${notifyMethod === 'email' ? 'active' : ''}`}
                    onClick={() => setNotifyMethod('email')}
                  >
                    <Mail size={18} />
                    Email
                  </button>
                  <button
                    type="button"
                    className={`notify-method-btn ${notifyMethod === 'sms' ? 'active' : ''}`}
                    onClick={() => setNotifyMethod('sms')}
                    disabled={!notifyEmployee.phone}
                    title={!notifyEmployee.phone ? 'No phone number on file' : 'Send SMS'}
                  >
                    <Phone size={18} />
                    SMS
                  </button>
                </div>
              </div>
              {notifyMethod === 'email' && (
                <div className="form-group">
                  <label className="label">Subject</label>
                  <input
                    type="text"
                    className="input-field"
                    value={notifyForm.subject}
                    onChange={(e) => setNotifyForm({ ...notifyForm, subject: e.target.value })}
                    required
                  />
                </div>
              )}
              <div className="form-group">
                <label className="label">Message</label>
                <textarea
                  className="input-field message-textarea"
                  rows={notifyMethod === 'sms' ? 3 : 6}
                  value={notifyForm.message}
                  onChange={(e) => setNotifyForm({ ...notifyForm, message: e.target.value })}
                  required
                  maxLength={notifyMethod === 'sms' ? 160 : undefined}
                />
                {notifyMethod === 'sms' && (
                  <p className="char-count">{notifyForm.message.length}/160 characters</p>
                )}
              </div>
              <p className="message-info">
                {notifyMethod === 'email' ? (
                  <><Mail size={14} /> Email will be sent to: {notifyEmployee.email || 'No email on file'}</>
                ) : (
                  <><Phone size={14} /> SMS will be sent to: {notifyEmployee.phone || 'No phone on file'}</>
                )}
              </p>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowNotifyModal(false)}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary btn-send" 
                  disabled={sendingNotification || (notifyMethod === 'email' && !notifyEmployee.email) || (notifyMethod === 'sms' && !notifyEmployee.phone)}
                >
                  <Send size={16} />
                  {sendingNotification ? 'Sending...' : `Send ${notifyMethod === 'sms' ? 'SMS' : 'Email'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyEmployees;
